import { excludeUndefinedDataKeys, hasObjectValue } from '@repo/common-utils';
import { NextRequest, NextResponse } from 'next/server';

import {
  BZNAV_UTM_KEY,
  MAINTENANCE_BYPASS_KEY,
  MAINTENANCE_SETTING_COOKIE,
  RESOURCE_FILE_PATTERNS,
  WORKING_PLATFORM_KEY,
  WORKING_PLATFORM_URL_QUERY_KEY
} from '../constants';
import { MaintenanceSetting, StorageValueByEnv, UtmData } from '../types';

const defaultStorageValue: Record<string, { isFetching: boolean; lastFetched: number; value: unknown }> = {};

const removeWorkingPlatformQueryParam = (nextUrl: NextRequest['nextUrl']) => {
  const redirectUrl = nextUrl.clone();
  redirectUrl.searchParams.delete(WORKING_PLATFORM_URL_QUERY_KEY);
  return redirectUrl;
};

export const getMiddlewareWorkingPlatformCookie = (request: NextRequest) => {
  const savedWorkingPlatform = request.cookies.get(WORKING_PLATFORM_KEY)?.value;
  const urlWorkingPlatform = request.nextUrl.searchParams.get(WORKING_PLATFORM_URL_QUERY_KEY);
  let workingPlatform: string | null = null;
  let redirectUrl: NextRequest['nextUrl'] | null = null;

  if (savedWorkingPlatform && savedWorkingPlatform === urlWorkingPlatform) {
    redirectUrl = removeWorkingPlatformQueryParam(request.nextUrl);
  } else {
    if (request.headers.get('x-app-bznav')) {
      workingPlatform = 'app-deprecated';
    } else if (urlWorkingPlatform) {
      redirectUrl = removeWorkingPlatformQueryParam(request.nextUrl);
      workingPlatform = urlWorkingPlatform;
    } else if (!savedWorkingPlatform) {
      workingPlatform = 'web';
    }
  }

  return { workingPlatform, redirectUrl };
};

export const setMiddlewareWorkingPlatformCookie = (response: NextResponse, workingPlatform: string | null) => {
  if (!workingPlatform) return;
  response.cookies.set(WORKING_PLATFORM_KEY, workingPlatform, { path: '/', secure: true });
};

export const setMiddlewareUtmCookie = (request: NextRequest, response: NextResponse) => {
  const savedUtmCookie = request.cookies.get(BZNAV_UTM_KEY)?.value;

  if (savedUtmCookie) {
    const savedUtm = JSON.parse(savedUtmCookie);

    if (savedUtm.utmSource !== 'referer') return response;
  }

  const uriUtm = excludeUndefinedDataKeys({
    utmSource: request.nextUrl.searchParams.get('utm_source') ?? undefined,
    utmMedium: request.nextUrl.searchParams.get('utm_medium') ?? undefined,
    utmCampaign: request.nextUrl.searchParams.get('utm_campaign') ?? undefined,
    utmContent: request.nextUrl.searchParams.get('utm_content') ?? undefined,
    utmTerm: request.nextUrl.searchParams.get('utm_term') ?? undefined
  }) as UtmData;

  if (hasObjectValue(uriUtm)) {
    response.cookies.set(BZNAV_UTM_KEY, JSON.stringify(uriUtm));
  }
};

export const setMiddlewareMaintenanceCookie = (response: NextResponse, maintenanceSetting: MaintenanceSetting, hasBypassQuery: boolean) => {
  if (maintenanceSetting.status) {
    response.cookies.set(MAINTENANCE_SETTING_COOKIE, JSON.stringify(maintenanceSetting), {
      path: '/',
      secure: true
    });

    if (hasBypassQuery) {
      response.cookies.set(MAINTENANCE_BYPASS_KEY, 'admin');
    } else {
      response.cookies.delete(MAINTENANCE_BYPASS_KEY);
    }
  } else {
    response.cookies.delete(MAINTENANCE_SETTING_COOKIE);
    response.cookies.delete(MAINTENANCE_BYPASS_KEY);
  }
};

export const checkBypassQuery = async (nextRequest: NextRequest) => {
  const bypassQuery = nextRequest.cookies.get(MAINTENANCE_BYPASS_KEY)?.value || nextRequest.nextUrl.searchParams.get('zent') || '';
  const hasBypassQuery = bypassQuery === 'admin';

  return hasBypassQuery;
};

export const getFirebaseStorageValue = async <T>({
  storageUrl,
  key,
  defaultValue,
  revalidate = 60000
}: {
  storageUrl: string;
  key: string;
  defaultValue: T;
  revalidate?: number;
}): Promise<T> => {
  if (defaultStorageValue[key]?.isFetching || Date.now() - (defaultStorageValue[key]?.lastFetched || 0) < revalidate) {
    return defaultStorageValue[key]?.value as T;
  }

  try {
    console.info('Start fetching storage value');

    if (!defaultStorageValue[key]) {
      defaultStorageValue[key] = { isFetching: false, lastFetched: 0, value: defaultValue };
    }

    defaultStorageValue[key].isFetching = true;
    const response = await fetch(storageUrl);
    if (!response.ok) throw new Error('Error fetching storage Value on middleware');
    const allEnvStorageValue: StorageValueByEnv<T> = await response.json();

    const zenv = process.env.NEXT_PUBLIC_ZENV ?? 'dev';
    defaultStorageValue[key].value = (allEnvStorageValue[zenv] as T) || defaultValue;
    defaultStorageValue[key].lastFetched = Date.now();
    console.info('storage value fetched successfully');
  } catch (error) {
    console.error(error);

    if (!defaultStorageValue[key]) {
      defaultStorageValue[key] = { isFetching: false, lastFetched: 0, value: defaultValue };
    }

    if (defaultStorageValue[key].value === undefined) {
      defaultStorageValue[key].value = defaultValue;
    }
  } finally {
    if (defaultStorageValue[key]) {
      defaultStorageValue[key].isFetching = false;
    }
  }

  return defaultStorageValue[key]?.value as T;
};

export const getRedirectResponse = (request: NextRequest, redirectUrl: string) => {
  const newUrl = new URL(redirectUrl, request.url);
  for (const [key, value] of request.nextUrl.searchParams.entries()) {
    newUrl.searchParams.set(key, value);
  }

  return NextResponse.redirect(newUrl);
};

export const bypassActionResponse = (request: NextRequest, nextUrl: NextRequest['nextUrl']) => {
  const host = request.headers.get('host');
  if (host?.includes('m.bznav.com')) {
    const redirectUrl = new URL(request.nextUrl.pathname + request.nextUrl.search, 'https://refund.bznav.com');
    return NextResponse.redirect(redirectUrl);
  } else if (nextUrl.pathname.startsWith('/ping') || nextUrl.pathname === '/.well-known/appspecific/com.chrome.devtools.json') {
    return NextResponse.json({ message: 'success' });
  } else if (RESOURCE_FILE_PATTERNS.some((value) => nextUrl.pathname.includes(value))) {
    return NextResponse.next();
  }

  return null;
};
